import { useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MonthPanel, monthFull } from "@/components/MonthPicker";

const pad = (n) => String(n).padStart(2, "0");
export const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const lastDay = (m) => { const [y, mo] = m.split("-").map(Number); return `${m}-${pad(new Date(y, mo, 0).getDate())}`; };
export const monthPeriod = (m) => ({ mode: "month", start: `${m}-01`, end: lastDay(m) });
export const periodMonth = (p) => p.start.slice(0, 7);
const br = (s) => `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
export const periodLabel = (p) => p.mode === "month" ? monthFull(periodMonth(p)) : p.mode === "day" ? br(p.start) : `${p.start.slice(8, 10)}/${p.start.slice(5, 7)} – ${br(p.end)}`;
export const periodShort = (p) => p.mode === "month" ? `mês ${monthFull(periodMonth(p)).slice(0, 3).toLowerCase()}/${p.start.slice(2, 4)}` : p.mode === "day" ? `dia ${p.start.slice(8, 10)}/${p.start.slice(5, 7)}` : `${p.start.slice(8, 10)}/${p.start.slice(5, 7)} – ${p.end.slice(8, 10)}/${p.end.slice(5, 7)}`;

export const loadPeriod = (key) => { try { const p = JSON.parse(localStorage.getItem(key)); if (p?.start && p?.end && p?.mode) return p; } catch {} return monthPeriod(new Date().toISOString().slice(0, 7)); };

const MODES = [["month", "Mês"], ["day", "Dia"], ["range", "Intervalo"]];

export const PeriodPicker = ({ value, onChange, marked = [], entries = [], testId = "period-picker", className = "" }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(value.mode);
  const [range, setRange] = useState(null);
  const month = periodMonth(value);
  const pick = (p) => { onChange(p); setOpen(false); };
  const entryPeriod = (e) => ({ mode: e.start === e.end ? "day" : e.start.endsWith("-01") && e.end === lastDay(e.start.slice(0, 7)) ? "month" : "range", start: e.start, end: e.end });
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setMode(value.mode); setRange(null); } }}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" data-testid={testId} className={`justify-between gap-2 font-normal min-w-[210px] ${className}`}>
          <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-primary" /><span className="capitalize num" data-testid={`${testId}-label`}>{periodLabel(value)}</span></span>
          <ChevronDown className="w-4 h-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-auto" data-testid={`${testId}-popover`}>
        <div className="flex items-center gap-2 p-2 border-b border-border">
          <div className="grid grid-cols-3 gap-1 rounded-lg bg-secondary p-1 text-xs flex-1">
            {MODES.map(([k, l]) => <button key={k} type="button" data-testid={`${testId}-mode-${k}`} onClick={() => setMode(k)} className={`rounded-md px-3 py-1.5 transition-colors ${mode === k ? "bg-card font-semibold shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
          </div>
        </div>
        {mode === "month" && open && <MonthPanel value={month} onChange={(ym) => pick(monthPeriod(ym))} marked={marked} testId={testId} />}
        {mode === "day" && (
          <div className="p-1" data-testid={`${testId}-day-calendar`}>
            <Calendar mode="single" locale={ptBR} defaultMonth={fromIso(value.start)} selected={value.mode === "day" ? fromIso(value.start) : undefined} onSelect={(d) => d && pick({ mode: "day", start: toIso(d), end: toIso(d) })} />
          </div>
        )}
        {mode === "range" && (
          <div className="p-1" data-testid={`${testId}-range-calendar`}>
            <Calendar mode="range" locale={ptBR} numberOfMonths={1} defaultMonth={fromIso(value.start)} selected={range || (value.mode === "range" ? { from: fromIso(value.start), to: fromIso(value.end) } : undefined)}
              onSelect={(r) => { setRange(r); if (r?.from && r?.to) pick({ mode: "range", start: toIso(r.from), end: toIso(r.to) }); }} />
            <p className="text-[11px] text-muted-foreground px-3 pb-2">Clique no primeiro e no último dia do intervalo.</p>
          </div>
        )}
        {mode !== "month" && !!entries.length && (
          <div className="border-t border-border p-3 space-y-1" data-testid={`${testId}-entries`}>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Lançamentos em {monthFull(month)}</p>
            <div className="flex flex-wrap gap-1.5">
              {entries.map((e) => { const p = entryPeriod(e); const sel = e.start === value.start && e.end === value.end; return (
                <button key={e.start + e.end} type="button" data-testid={`${testId}-entry-${e.start}_${e.end}`} onClick={() => pick(p)} className={`num text-xs rounded-md px-2 py-1 border transition-colors ${sel ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>{e.start === e.end ? e.start.slice(8) : `${e.start.slice(8)}–${e.end.slice(8)}`}</button>
              ); })}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
